import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { doTien, soTien } from '../../chung/nghiep-vu/so-tien';
import { TRONG } from '../../chung/nghiep-vu/thoi-gian';
import type { TrangThaiGia } from '../../chung/nghiep-vu/types-vet';
import { useNguonVet } from '../nguon';
import { coGiaThieu } from '../nghiep-vu/vet';

/**
 * Các nhãn nói ra mức tin được của một con số, và hai ô số có luật riêng — port `vet/NhanTinCay.tsx` native.
 *
 * Chỉ cổng nền tảng dùng: tenant chỉ thấy token Fox (ADR-0123 mục 1, 5).
 *
 * ── BỐN CỜ, BỐN VIỆC KHÁC NHAU — KHÔNG GỘP ──────────────────────────────
 * • `chua_co_gia` — THIẾU DÒNG GIÁ VỐN. Việc cần làm là khai giá vốn. Riêng ca `thieu_ty_gia` (có dòng giá, thiếu tỷ giá)
 *   cũng là `chua_co_gia`, nhưng việc cần làm là bổ sung tỷ giá — nhãn riêng.
 * • `canh_bao` — cảnh báo về chính phép QUY ĐỔI token; việc cần làm là sửa bộ quy đổi.
 * • `uoc_tinh` — tiền tính bằng giá suy ra, không phải giá đã khai.
 * • `dang_tin === false` (chỉ ở mức trace) — trong chuỗi có ít nhất một lượt mang cảnh báo quy đổi.
 *
 * `Badge` của kho không nhận `title`, nên câu giải thích đặt ở thẻ bọc.
 */

const LIEN_KET =
  'rounded text-xs text-content-link underline decoration-dotted underline-offset-2 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600';

function CoGiaiThich({ moTa, children }: { moTa: string; children: ReactNode }) {
  return (
    <span title={moTa} className="inline-flex">
      {children}
    </span>
  );
}

export function NhanChuaCoGia() {
  const { t } = useT();
  return (
    <CoGiaiThich moTa={t('nktvSuDungAi.co.chuaCoGiaMoTa')}>
      <Badge variant="warning">{t('nktvSuDungAi.co.chuaCoGia')}</Badge>
    </CoGiaiThich>
  );
}

export function NhanThieuTyGia() {
  const { t } = useT();
  return (
    <CoGiaiThich moTa={t('nktvSuDungAi.co.thieuTyGiaMoTa')}>
      <Badge variant="warning">{t('nktvSuDungAi.co.thieuTyGia')}</Badge>
    </CoGiaiThich>
  );
}

/** Lối sang bảng giá model cho mô hình đang thiếu giá — chỉ cổng nền tảng, và chỉ khi người xem mở được bảng giá. */
export function LienKetKhaiGia({ moHinh }: { moHinh: string }) {
  const { t } = useT();
  const duongDan = useDuongDan();
  const { lienKetBangGia } = useNguonVet();
  if (!lienKetBangGia) return null;
  return (
    <Link
      to={duongDan(lienKetBangGia)}
      title={t('nktvSuDungAi.co.khaiGiaMoTa', { moHinh })}
      onClick={(e) => e.stopPropagation()}
      className={LIEN_KET}
    >
      {t('nktvSuDungAi.co.khaiGia')}
    </Link>
  );
}

function ThieuTyGiaKemLienKet() {
  const { t } = useT();
  const duongDan = useDuongDan();
  const { lienKetBangGia } = useNguonVet();
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <NhanThieuTyGia />
      {lienKetBangGia && (
        <Link
          to={duongDan(lienKetBangGia)}
          title={t('nktvSuDungAi.co.xemTyGiaMoTa')}
          onClick={(e) => e.stopPropagation()}
          className={LIEN_KET}
        >
          {t('nktvSuDungAi.co.xemTyGia')}
        </Link>
      )}
    </span>
  );
}

/** Nhãn "chưa có giá" ở mức GOM (chuỗi, luồng). */
export function NhanGiaThieu({
  coChuaCoGia,
  coThieuTyGia,
  coThieuDongGia,
}: {
  coChuaCoGia: boolean | null | undefined;
  coThieuTyGia?: boolean | null;
  coThieuDongGia?: boolean | null;
}) {
  const co = coGiaThieu(coChuaCoGia, coThieuTyGia, coThieuDongGia);
  return (
    <>
      {co.chuaCoGia && <NhanChuaCoGia />}
      {co.thieuTyGia && <NhanThieuTyGia />}
    </>
  );
}

export function NhanCanhBao({ canhBao }: { canhBao: readonly string[] }) {
  const { t, locale } = useT();
  if (canhBao.length === 0) return null;
  // Mã cảnh báo của bộ chuẩn hoá là thứ đội kỹ thuật tra được; rút gọn thành một câu chung là vứt đi phần sửa được.
  return (
    <CoGiaiThich moTa={`${t('nktvSuDungAi.co.canhBaoMoTa')} — ${canhBao.join(', ')}`}>
      <Badge variant="danger">
        {t('nktvSuDungAi.co.canhBao', { so: canhBao.length.toLocaleString(locale) })}
      </Badge>
    </CoGiaiThich>
  );
}

export function NhanUocTinh() {
  const { t } = useT();
  return (
    <span className="text-xs text-content-muted" title={t('nktvSuDungAi.co.uocTinhMoTa')}>
      {t('nktvSuDungAi.co.uocTinh')}
    </span>
  );
}

export function NhanKhongDangTin() {
  const { t } = useT();
  return (
    <CoGiaiThich moTa={t('nktvSuDungAi.co.khongDangTinMoTa')}>
      <Badge variant="danger">{t('nktvSuDungAi.co.khongDangTin')}</Badge>
    </CoGiaiThich>
  );
}

/**
 * Ô giá vốn của một lượt: `chua_co_gia` **không hiện 0 đồng** — một ô "0 ₫" trong bảng đối soát là một lượt gọi miễn phí.
 */
export function NhanTrangThaiGiaVon({
  trangThai,
  thieuTyGia,
  moHinh,
  giaTri,
}: {
  trangThai: TrangThaiGia | null | undefined;
  thieuTyGia?: boolean | null;
  /** Có thì ô "Chưa có giá vốn" kèm lối sang giá vốn model. */
  moHinh?: string | null;
  giaTri: ReactNode;
}) {
  if (trangThai === 'chua_co_gia' && thieuTyGia === true) return <ThieuTyGiaKemLienKet />;
  if (trangThai === 'chua_co_gia') {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-2">
        <NhanChuaCoGia />
        {moHinh ? <LienKetKhaiGia moHinh={moHinh} /> : null}
      </span>
    );
  }
  return <>{giaTri}</>;
}

/** Ô giá vốn — chỉ cổng nền tảng, chỉ khi API trả `chi_phi_von` (người xem có `AI_PRICING.READ`). */
export function GiaVon({ giaTri, donVi }: { giaTri: string | null | undefined; donVi: string | null }) {
  const { locale } = useT();
  return (
    <span className="font-mono text-xs tabular-nums text-content-secondary">
      {soTien(giaTri ?? null, donVi, locale) ?? TRONG}
    </span>
  );
}

/**
 * Ô tiết kiệm nhờ đệm. **Được phép ÂM** (QĐ-15): đọc lại từ đệm có thể đắt hơn đọc mới, và giá trị âm là kết quả thật.
 * Không kẹp về 0, không ẩn — chỉ đổi tông để con số âm không trông như một khoản tiết kiệm.
 */
export function TietKiemDem({ giaTri, donVi }: { giaTri: string | null; donVi: string | null }) {
  const { t, locale } = useT();
  const so = doTien(giaTri);
  if (so === null) return <span className="text-xs text-content-muted">{TRONG}</span>;
  const am = so < 0;
  return (
    <span
      className={cn('font-mono text-xs tabular-nums', am ? 'text-danger-700' : 'text-content-primary')}
      title={am ? t('nktvSuDungAi.co.tietKiemAm') : undefined}
    >
      {soTien(so, donVi, locale) ?? TRONG}
    </span>
  );
}
